import { create } from 'zustand';
import type {
  JsonStructureCache,
  JsonStructureCachePath,
  JsonStructureCachePathType,
} from '@/types/electron';
import { upsertJsonStructureCache as defaultUpsert } from '../services/metaInfoApi';
import { walkJsonPaths } from '../utils/jsonPathWalker';

// Re-exported for existing callers/tests that import `walkJsonPaths` from
// this module (see `jsonPathWalker.ts`'s doc comment for why the pure walker
// lives in its own file — the Data Import suggestion engine needs it without
// this store's zustand/`metaInfoApi` baggage).
export { walkJsonPaths };

export const MAX_SAMPLES_PER_COLUMN = 1000;
export const IDLE_FLUSH_DEBOUNCE_MS = 5000;

export type ColumnKey = {
  connectionId: string;
  database: string;
  schema: string;
  table: string;
  column: string;
};

export type ColumnAggregate = {
  key: ColumnKey;
  /** Total samples pushed in this session. Drives the cap, NOT prevalence. */
  pathTotal: number;
  /**
   * Number of samples whose root value was walkable — i.e. an object OR an
   * array of objects (both shapes contribute paths). Prevalence divides by
   * this so root primitives don't deflate the percentages.
   */
  walkedSampleCount: number;
  pathSeen: Map<string, number>;
  pathTypes: Map<string, Set<JsonStructureCachePathType>>;
  dirty: boolean;
};

type State = {
  aggregates: Record<string, ColumnAggregate>;
};

type UpsertFn = (
  connectionId: string,
  database: string,
  entry: JsonStructureCache,
) => Promise<unknown>;

type Internals = {
  timers: Map<string, ReturnType<typeof setTimeout>>;
  upsertImpl: UpsertFn;
};

type Actions = {
  pushSample: (key: ColumnKey, value: unknown) => void;
  flush: (keyId: string) => Promise<void>;
  /**
   * Synchronous trigger for the same flush logic the debounce uses.
   * Cancels the pending debounce timer (if any) and immediately runs the
   * upsert pipeline; returns the upsert promise so callers can await
   * persistence (e.g. before invalidating MetaInfo).
   */
  flushNow: (keyId: string) => Promise<void>;
  flushAll: () => Promise<void>;
  reset: () => void;
  /** Test seam — replace the IPC writer. Returns the previous one. */
  __setUpsertImpl: (impl: UpsertFn) => UpsertFn;
};

const internals: Internals = {
  timers: new Map(),
  upsertImpl: defaultUpsert,
};

export function keyId(k: ColumnKey): string {
  return `${k.connectionId}|${k.database}|${k.schema}|${k.table}|${k.column}`;
}

/**
 * Walk one sample into an aggregate: per-path counts, per-path type unions,
 * pathTotal / walkedSampleCount bookkeeping, dirty flag. Shared between the
 * whole-column store (pushSample) and the ephemeral filtered store.
 * No-op once the aggregate hit MAX_SAMPLES_PER_COLUMN.
 */
export function applySampleToAggregate(agg: ColumnAggregate, value: unknown): void {
  if (agg.pathTotal >= MAX_SAMPLES_PER_COLUMN) return;
  const isWalkable = value !== null && typeof value === 'object';
  const seenInThisRow = new Set<string>();
  walkJsonPaths(value, (path, type) => {
    let typeSet = agg.pathTypes.get(path);
    if (!typeSet) {
      typeSet = new Set();
      agg.pathTypes.set(path, typeSet);
    }
    typeSet.add(type);
    if (seenInThisRow.has(path)) return;
    seenInThisRow.add(path);
    agg.pathSeen.set(path, (agg.pathSeen.get(path) ?? 0) + 1);
  });
  agg.pathTotal += 1;
  if (isWalkable) agg.walkedSampleCount += 1;
  agg.dirty = true;
}

/**
 * Project an aggregate into the popover's flat path list. Prevalence divides
 * by pathTotal (session-view semantics — matches the popover's historical
 * behavior; the flush-to-MetaInfo path keeps its walkedSampleCount divisor).
 */
export function aggregateToPaths(
  agg: ColumnAggregate,
): { paths: JsonStructureCachePath[]; sampledRows: number } {
  const paths: JsonStructureCachePath[] = [];
  for (const [path, count] of agg.pathSeen.entries()) {
    const typeSet = agg.pathTypes.get(path) ?? new Set<JsonStructureCachePathType>();
    const type: JsonStructureCachePathType =
      typeSet.size > 1 ? 'mixed' : (typeSet.values().next().value ?? 'mixed');
    paths.push({ path, type, prevalence: count / agg.pathTotal });
  }
  paths.sort((a, b) => b.prevalence - a.prevalence || a.path.localeCompare(b.path));
  return { paths, sampledRows: agg.pathTotal };
}

const initialState: State = {
  aggregates: {},
};

export const useJsonStructureAggregatorStore = create<State & Actions>(
  (set, get) => ({
    ...initialState,

    pushSample: (key, value) => {
      const id = keyId(key);
      const existing = get().aggregates[id];
      const agg: ColumnAggregate = existing ?? {
        key,
        pathTotal: 0,
        walkedSampleCount: 0,
        pathSeen: new Map(),
        pathTypes: new Map(),
        dirty: false,
      };
      if (agg.pathTotal >= MAX_SAMPLES_PER_COLUMN) {
        // Cap reached for the session — stop accepting until reset.
        return;
      }
      applySampleToAggregate(agg, value);
      set((s) => ({ aggregates: { ...s.aggregates, [id]: agg } }));
      scheduleDebouncedFlush(id);
    },

    // `flush` and `flushNow` share the same implementation: cancel the
    // pending debounce timer (if any) and run the upsert pipeline. The
    // debounced timer also calls into this same path — there is no parallel
    // implementation. `flushNow` exists as a named alias so callers
    // (e.g. server-scan completion) can express intent at call sites.
    flush: async (id) => runFlush(id),
    flushNow: async (id) => runFlush(id),

    flushAll: async () => {
      const ids = Object.keys(get().aggregates);
      await Promise.all(ids.map((id) => runFlush(id)));
    },

    reset: () => {
      for (const t of internals.timers.values()) clearTimeout(t);
      internals.timers.clear();
      set({ aggregates: {} });
    },

    __setUpsertImpl: (impl) => {
      const prev = internals.upsertImpl;
      internals.upsertImpl = impl;
      return prev;
    },
  }),
);

async function runFlush(id: string): Promise<void> {
  const timer = internals.timers.get(id);
  if (timer) {
    clearTimeout(timer);
    internals.timers.delete(id);
  }
  const store = useJsonStructureAggregatorStore;
  const agg = store.getState().aggregates[id];
  if (!agg || !agg.dirty || agg.pathTotal === 0) return;

  // Prevalence denominator is the count of WALKABLE samples — both objects
  // and arrays (now that array elements are enumerated as `[*]` paths). Root
  // primitives still contribute nothing and are excluded from the divisor so
  // a column whose paths are all present in every walkable row reads 100%.
  const denominator = Math.max(1, agg.walkedSampleCount);
  const paths: JsonStructureCachePath[] = [];
  for (const [path, count] of agg.pathSeen.entries()) {
    const typeSet = agg.pathTypes.get(path) ?? new Set();
    const type: JsonStructureCachePathType =
      typeSet.size > 1 ? 'mixed' : (typeSet.values().next().value ?? 'mixed');
    paths.push({
      path,
      type,
      prevalence: count / denominator,
    });
  }
  paths.sort((a, b) => b.prevalence - a.prevalence || a.path.localeCompare(b.path));

  const entry: JsonStructureCache = {
    schema: agg.key.schema,
    table: agg.key.table,
    column: agg.key.column,
    paths,
    sampledRows: agg.pathTotal,
    sampledAt: new Date().toISOString(),
  };

  try {
    await internals.upsertImpl(agg.key.connectionId, agg.key.database, entry);
    agg.dirty = false;
    store.setState((s) => ({ aggregates: { ...s.aggregates, [id]: agg } }));
  } catch {
    // Stay dirty so the next flush trigger retries; never throw out of
    // the passive aggregation pipeline (it must not crash the renderer).
  }
}

function scheduleDebouncedFlush(id: string): void {
  const existing = internals.timers.get(id);
  if (existing) clearTimeout(existing);
  const timer = setTimeout(() => {
    internals.timers.delete(id);
    const ric =
      typeof globalThis !== 'undefined' &&
      (globalThis as { requestIdleCallback?: (cb: () => void) => void })
        .requestIdleCallback;
    const run = () => {
      // Reuses the same flush pathway as `flush` / `flushNow`.
      void useJsonStructureAggregatorStore.getState().flushNow(id);
    };
    if (typeof ric === 'function') {
      ric(run);
    } else {
      run();
    }
  }, IDLE_FLUSH_DEBOUNCE_MS);
  internals.timers.set(id, timer);
}
